import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import riskPremiumCalculator, {
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

interface RiskPremiumCalculation {
  amount: number;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  quotedCurrency: string;
  warrantyRates?: RiskPremiumRates;
  type: string;
}

const RiskPremiumCalculator: React.FC<RiskPremiumCalculation> = ({
  amount,
  startDate,
  endDate,
  quotedCurrency,
  warrantyRates,
  type,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const businessIdentifier = auth.user?.businessIdentifier ?? null;
  const { calculate } = riskPremiumCalculator();
  const [totalPremium, setTotalPremium] = useState(0);
  const { data } = getCreditLineByProduct(dispatch, businessIdentifier, 'sgr');

  const rates = useMemo(() => {
    if (warrantyRates) return warrantyRates;
    const subProductsData = data?.subProducts || [];
    if (subProductsData.length > 0 && type) {
      const subProduct = subProductsData.find((sp) => sp.subProductId === type);
      return {
        variableRate: subProduct?.operationCostVariable ?? null,
        fixedRate: subProduct?.operationCostFixed ?? null,
        variableDiscount: subProduct?.discountRateVariable ?? null,
        fixedDiscount: subProduct?.discountRateFixed ?? null,
      };
    }
    return undefined;
  }, [data, type]);

  useEffect(() => {
    const performCalculation = () => {
      try {
        const premium = calculate({
          quotedAmount: amount,
          startDate: startDate,
          endDate: endDate,
          quotedCurrency,
          rates,
        });
        setTotalPremium(premium?.amount ?? 0);
      } catch (error) {
        console.error(error);
        setTotalPremium(0);
      }
    };

    performCalculation();
  }, [amount, startDate, endDate, quotedCurrency, rates]);

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Costo de emisión
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>
      <Alert
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
        sx={{
          borderRadius: 0.5,
          mt: 1.5,
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          py: 0.75,
          '& .MuiAlert-message': { py: 0.25 },
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          Información Importante
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
        >
          Este es un valor referencial. El monto total a pagar puede variar al
          momento de hacer efectiva la solicitud de la garantía.
        </Typography>
      </Alert>
    </Paper>
  );
};

export default RiskPremiumCalculator;
